"use client";

import { TRIGGER_TIME_OPTIONS } from "@/lib/agents/catalog/trigger-rows";
import { cn } from "@/lib/utils";

import { PopoverMenuDatePicker } from "./popover-menu-date-picker";
import { Select } from "@/components/ui/select";

const fieldButtonClass =
  "flex w-full min-w-0 items-center justify-start gap-0.5 rounded-md bg-[#f6f6f6] px-1.5 py-0.5 text-left text-[14px] tracking-[-0.01em] text-[#111] transition-colors hover:bg-[#eee] disabled:pointer-events-none disabled:opacity-50";

const timeTriggerClassName =
  "inline-flex shrink-0 items-center gap-0.5 rounded-md bg-[#f6f6f6] px-1.5 py-0.5 text-[14px] tracking-[-0.01em] text-[#111] transition-colors hover:bg-[#eee] disabled:pointer-events-none disabled:opacity-50";

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

/** Snap to hourly options used by schedule triggers (`09:00`, etc.). */
function normalizeTimeValue(time: string): string {
  const [hh] = time.split(":");
  const hour = Number.parseInt(hh ?? "", 10);
  if (!Number.isFinite(hour)) return "09:00";
  return `${pad2(Math.min(23, Math.max(0, hour)))}:00`;
}

type Props = {
  date: string;
  time: string;
  onDateChange: (next: string) => void;
  onTimeChange: (next: string) => void;
  disabled?: boolean;
  className?: string;
};

/**
 * Date + time controls for task scheduling. Date uses PopoverMenu calendar;
 * time uses the same hourly Select as schedule triggers.
 */
export function SystemDateTimeFields({
  date,
  time,
  onDateChange,
  onTimeChange,
  disabled = false,
  className,
}: Props) {
  const timeValue = normalizeTimeValue(time);

  return (
    <div className={cn("flex w-full items-center gap-2", className)}>
      <div className="relative min-w-0 flex-1">
        <PopoverMenuDatePicker
          value={date}
          onChange={onDateChange}
          disabled={disabled}
          ariaLabel="Start date"
          rowClassName="w-full"
          triggerClassName={fieldButtonClass}
        />
      </div>

      <div className="relative shrink-0">
        <Select
          ariaLabel="Start time"
          value={timeValue}
          options={TRIGGER_TIME_OPTIONS}
          onChange={onTimeChange}
          disabled={disabled}
          triggerClassName={timeTriggerClassName}
        />
      </div>
    </div>
  );
}
